import { create } from 'zustand';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../firebaseConfig';
import api from '../common/api/api';

type FCMPermission = NotificationPermission;

interface PushNotificationState {
  isSupported: boolean;
  permission: FCMPermission;
  token: string | null;
  isRegistered: boolean;
  registeredForUserId: string | null;
  error: string | null;
  requestPermission: (userId: string) => Promise<boolean>;
  syncToken: (userId: string) => Promise<void>;
}

let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (!swRegistrationPromise) {
    swRegistrationPromise = navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .catch((err) => {
        console.error('SW registration failed', err);
        // Allow a later call to retry instead of being stuck on a failed registration forever.
        swRegistrationPromise = null;
        return null;
      });
  }
  return swRegistrationPromise;
}

let foregroundListenerAttached = false;

// Registers the service worker once and wires up the foreground message
// listener. Safe to call multiple times (e.g. React Strict Mode double-invoke)
// since registerServiceWorker() memoizes its promise and foregroundListenerAttached
// guards onMessage from being subscribed twice.
export function initPushNotificationStore(): void {
  if (typeof window === 'undefined') return;
  registerServiceWorker();
  if (messaging && !foregroundListenerAttached) {
    foregroundListenerAttached = true;
    onMessage(messaging, (payload) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('FCM foreground message received:', payload);
      }
    });
  }
}

const usePushNotificationStore = create<PushNotificationState>((set, get) => {
  const fetchToken = async (): Promise<string | null> => {
    if (!messaging) return null;
    try {
      const registration = await registerServiceWorker();
      if (!registration) {
        set({ error: 'Service worker unavailable' });
        return null;
      }
      const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        set({ error: 'VAPID key not configured' });
        return null;
      }
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      set({ error: 'Failed to get FCM token' });
      return null;
    }
  };

  const registerWithBackend = async (userId: string, token: string): Promise<boolean> => {
    try {
      const response = await api.post('fcm-token', { user_id: userId, fcm_token: token });
      if (response.success) {
        return true;
      }
      console.error('Failed to register FCM token:', response.data);
      return false;
    } catch (error) {
      console.error('Error registering FCM token:', error);
      return false;
    }
  };

  // Single source of truth for permission/token sync. Called from hook mount,
  // permission-change events, window focus, and once auth is confirmed ready
  // (from useAuth) so the backend POST never races ahead of the Authorization
  // header being set. Guarded against concurrent calls for the same user since
  // mount/focus/auth-ready can all fire in quick succession on page load.
  let syncInFlight: { userId: string; promise: Promise<void> } | null = null;

  const runSync = async (userId: string): Promise<void> => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && !!messaging;
    set({ isSupported });
    if (!isSupported) return;

    const permission = Notification.permission;
    set({ permission });

    if (permission === 'granted') {
      const token = await fetchToken();
      if (!token) {
        // Clear any stale token so consumers don't act on a token that's no
        // longer valid just because a transient fetch failed.
        set({ token: null, isRegistered: false, registeredForUserId: null });
        return;
      }

      const alreadyRegistered = get().token === token
        && get().registeredForUserId === userId
        && get().isRegistered;
      set({ token });
      if (alreadyRegistered) return;

      const registered = await registerWithBackend(userId, token);
      set({ isRegistered: registered, registeredForUserId: registered ? userId : null });
    } else if (permission === 'denied') {
      set({ token: null, isRegistered: false, registeredForUserId: null });
    }
  };

  const syncPermissionAndToken = (userId: string): Promise<void> => {
    if (!userId || typeof window === 'undefined') return Promise.resolve();
    if (syncInFlight?.userId === userId) return syncInFlight.promise;

    const promise = runSync(userId).finally(() => {
      if (syncInFlight?.userId === userId) syncInFlight = null;
    });
    syncInFlight = { userId, promise };
    return promise;
  };

  return {
    isSupported: false,
    permission: typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default',
    token: null,
    isRegistered: false,
    registeredForUserId: null,
    error: null,

    requestPermission: async (userId: string): Promise<boolean> => {
      try {
        const permission = await Notification.requestPermission();
        set({ permission });
        if (permission === 'granted') {
          await syncPermissionAndToken(userId);
          return get().isRegistered;
        }
        console.warn('Notification permission denied');
        return false;
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        set({ error: 'Failed to request permission' });
        return false;
      }
    },

    syncToken: async (userId: string) => {
      await syncPermissionAndToken(userId);
    },
  };
});

export default usePushNotificationStore;
